import { app } from './state.js';
import { initRouter, back } from './router.js';
import { loadManifest, findSeries, findCourse } from './manifest.js';
import { SeriesGrid } from './ui/SeriesGrid.js';
import { CourseList } from './ui/CourseList.js';
import { createCoursePlayer, type CoursePlayerHandle } from './ui/CoursePlayer.js';
import { bindKeys } from './keybinds.js';
import { h, clear } from './dom.js';
import { SiteNav } from './ui/SiteNav.js';

const root = document.getElementById('app')!;
let currentPlayer: CoursePlayerHandle | null = null;
let currentPlayerKey: string | null = null;

function destroyCurrentPlayer(): void {
  currentPlayer?.destroy();
  currentPlayer = null;
  currentPlayerKey = null;
}

function render(): void {
  const s = app.get();

  const show = (node: Node): void => {
    clear(root);
    root.append(SiteNav(), node);
  };

  if (s.error) {
    destroyCurrentPlayer();
    show(h('div', { className: 'error-banner', text: s.error }));
    return;
  }
  if (!s.manifest) {
    destroyCurrentPlayer();
    show(h('div', { className: 'loading', text: '正在加载课程清单…' }));
    return;
  }

  const r = s.route;
  if (r.name === 'home') {
    destroyCurrentPlayer();
    show(SeriesGrid(s.manifest.series));
    return;
  }
  const series = findSeries(s.manifest, r.seriesId);
  if (!series) {
    destroyCurrentPlayer();
    show(h('div', { className: 'error-banner', text: `未找到系列: ${r.seriesId}` }));
    return;
  }
  if (r.name === 'series') {
    destroyCurrentPlayer();
    show(CourseList(series));
    return;
  }
  const course = findCourse(series, r.courseId);
  if (!course) {
    destroyCurrentPlayer();
    show(h('div', { className: 'error-banner', text: `未找到课程: ${r.courseId}` }));
    return;
  }

  const key = `${series.id}::${course.id}`;
  if (currentPlayerKey === key && currentPlayer) return;
  destroyCurrentPlayer();
  const player = createCoursePlayer({
    series,
    course,
    initialTime: r.t,
    initialSentence: r.i,
    onTimeChange: throttlePersist((t, i) => persist(series.id, course.id, t, i)),
  });
  currentPlayer = player;
  currentPlayerKey = key;
  show(player.root);
}

function throttlePersist<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  let last = 0;
  return (...args: A) => {
    const now = Date.now();
    if (now - last < 2000) return;
    last = now;
    fn(...args);
  };
}

function persist(seriesId: string, courseId: string, t: number, i: number): void {
  try {
    localStorage.setItem(
      'mn:lastPlayed',
      JSON.stringify({ s: seriesId, c: courseId, t: Math.round(t * 100) / 100, i }),
    );
  } catch (_) {}
}


function setupAutoplayGate(): void {
  const KEY = 'mn:audioUnlocked';
  if (localStorage.getItem(KEY) === '1') return;
  const overlay = h('div', { className: 'gate' }, [
    h('div', { className: 'gate__card' }, [
      h('div', { className: 'gate__title', text: '点击任意处开始' }),
      h('div', { className: 'gate__hint', text: '浏览器会拦截无用户交互的自动播放，先点一下解锁。' }),
      h('div', { className: 'gate__key', text: '快捷键：Space 播/停 · ← → 上下句 · ↑ ↓ 上下课' }),
    ]),
  ]);
  document.body.appendChild(overlay);
  const unlock = () => {
    localStorage.setItem(KEY, '1');
    overlay.classList.add('gate--hide');
    setTimeout(() => overlay.remove(), 220);
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
}

async function main(): Promise<void> {
  initRouter();
  setupAutoplayGate();
  app.subscribe(() => render());
  render();

  bindKeys({
    onSpace: () => currentPlayer?.toggle(),
    onPrevSentence: () => currentPlayer?.prevSentence(),
    onNextSentence: () => currentPlayer?.nextSentence(),
    onPrevCourse: () => currentPlayer?.prevCourse(),
    onNextCourse: () => currentPlayer?.nextCourse(),
    onSeekBack: () => currentPlayer?.skip(-5),
    onSeekFwd: () => currentPlayer?.skip(5),
    onBackspace: () => back(),
  });

  try {
    const manifest = await loadManifest('series.json');
    app.set((s) => ({ ...s, manifest }));
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    app.set((s) => ({ ...s, error: `加载 series.json 失败: ${msg}` }));
  }
}

main();
